import type { Asset, DemoDoc, Layout, NormRect, Step } from '@mado/schema';
import {
  cuePlanDesktop,
  guideBeats,
  shotLayout,
  guidedStep,
  stepRest,
  scrollLayout,
  scrollStoryBeats,
  stepCamera,
  storyBeats,
  type Motion,
  type PxRect,
} from '@mado/schema/camera';

/**
 * How wide a step's picture is asked for: the `sizes` its <picture> carries,
 * and the width prefetch warms. One pure function for both, so prefetch
 * requests exactly the file the step will show (docs/build-log.md,
 * "Zoomed shots stay sharp").
 *
 * `sizes` is chosen once, at mount, for the widest the picture will ever be
 * drawn during the step's life — not its resting framing — because swapping
 * `sizes` mid-move would start a second download and swap the picture while
 * it travels. The framings a step can reach:
 *   - its target (the camera's rest, or the story's last shot) and its rest
 *     layout (where a camera move starts or pulls back to);
 *   - every beat of a phone story walk (a `result` lead-in can be tighter
 *     than the target);
 *   - a scroll step's framing, whose scale does not change with progress —
 *     on a phone story, the column walk (`scrollStoryBeats`), which reads
 *     the page's column at a far tighter zoom than the plain scroll;
 *   - a camera pan from another step on the same picture: the incoming step
 *     starts at the outgoing step's framing. Taking the widest over every
 *     step drawing the same asset covers it, and gives those steps one URL,
 *     so a pan reuses the decoded file instead of fetching a second size.
 * A resize is the one case not known up front: `Stage.relayout` takes the
 * new width and only ever grows `sizes`.
 */

export interface SizingView {
  viewport: { w: number; h: number };
  allowFocus: boolean;
  motion?: Motion;
  /** Phone story: the picture area a step is framed in; null or absent when no story is told. */
  pictureFor?: (step: Step) => PxRect | null;
  /** Phone walkthrough: the demo's one close-up zoom (`walkZoom`), so the file asked for covers it. */
  zoom?: number;
  /** What a whole-screen view shows (`overviewFrame`): the page's content box. Absent: the full capture. */
  frame?: NormRect;
}

/** CSS width an asset is drawn at under `layout` — never less than the viewport, as before focus existed. */
export function drawnWidth(asset: Asset, layout: Layout, viewport: { w: number }): number {
  return Math.max(viewport.w, asset.w * layout.scale);
}

/** The asset a step's stage draws: the stitched tall image for a scroll step. */
export function drawnAssetId(step: Step): string {
  return step.scroll ? step.scroll.assetTall : step.asset;
}

/** Every framing `step` can rest at or pass through on its own, under `view`. */
export function stepLayouts(step: Step, asset: Asset, view: SizingView): Layout[] {
  const { viewport, allowFocus, motion } = view;
  const picture = view.pictureFor?.(step) ?? null;
  if (step.scroll) {
    if (picture) {
      const input = { picture, asset, scroll: step.scroll, focus: step.focus };
      return scrollStoryBeats(input, true).map((b) => b.layout);
    }
    return [scrollLayout({ viewport, asset, focus: step.focus, allowFocus }, step.scroll, 0)];
  }
  if (picture) {
    const layouts = storyBeats(step, picture, asset, motion, {
      ...(view.zoom ? { zoom: view.zoom } : {}),
      ...(view.frame ? { frame: view.frame } : {}),
    }).map((b) => b.layout);
    // An overview step rests on the whole screen; a spread takes it to its
    // close-up (stage.ts `closeLayout`), which the file asked for must cover.
    if (view.zoom) {
      for (const b of storyBeats(step, picture, asset, motion, {
        zoom: view.zoom,
        view: 'action',
      }))
        layouts.push(b.layout);
    }
    // The pull-back and the pinch rest on the whole screen's content box.
    if (view.frame) layouts.push(shotLayout(picture, asset, view.frame));
    // A cue zooms into its rect inside the picture.
    for (const c of step.cues ?? [])
      if (c.effect === 'zoom') layouts.push(shotLayout(picture, asset, c.rect));
    // The planned phone track's frames (docs/plans/phone-track.md).
    for (const f of step.phone?.frames ?? [])
      if (f.kind !== 'glance') layouts.push(shotLayout(picture, asset, f.rect));
    return layouts;
  }
  const layouts = [
    stepCamera(step, { viewport, asset, allowFocus, motion }),
    stepRest(step, { viewport, asset, allowFocus }),
  ];
  // A desktop guided step passes through its regions (`guideBeats`), which can
  // be tighter than its target.
  if (guidedStep(step, motion)) {
    for (const b of guideBeats(step, { viewport, asset, allowFocus, motion }, { establish: true }))
      layouts.push(b.layout);
    if (step.cues?.length)
      for (const b of cuePlanDesktop(step, { viewport, asset, allowFocus, motion }, null).beats)
        layouts.push(b.layout);
  }
  return layouts;
}

/**
 * The `sizes` width for `step`: the widest its picture is drawn by it or by
 * any other step drawing the same asset. 0 when the asset is missing.
 */
export function stepSizes(doc: DemoDoc, step: Step, view: SizingView): number {
  const id = drawnAssetId(step);
  const asset = doc.assets[id];
  if (!asset) return 0;
  let w = view.viewport.w;
  for (const s of doc.steps) {
    if (s !== step && drawnAssetId(s) !== id) continue;
    for (const l of stepLayouts(s, asset, view))
      w = Math.max(w, drawnWidth(asset, l, view.viewport));
  }
  return w;
}
